import { FC, useState, useEffect } from "react";
import { RbacViewProps } from "./rbac.interfaces";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  LinearProgress,
  Grid,
  Tabs,
  Tab,
} from "@mui/material";
import MainTitle from "../../components/mainTitle";
import { useRbacHook, useRoleUserCountsHook } from "../../../hooks/useRbacHook";
import { useInstituteContext } from "../../../context/institute.context";
import RoleListView from "./roleList.view";
import AddRoleDialog from "./addRoleDialog";
import DeleteRoleDialog from "./deleteRoleDialog";
import RoleMatrixView from "./roleMatrix.view";
import RoleUsersView from "./roleUsers.view";
import { TRole } from "../../../apis/rbac/rbac.interfaces";

const RbacView: FC<RbacViewProps> = () => {
  const { selectedInstitute } = useInstituteContext();
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const { roles, isLoading, error } = useRbacHook({ selectedRoleId });
  const { roleUserCounts } = useRoleUserCountsHook(selectedRoleId);
  const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
  const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<TRole | null>(null);
  const [detailTab, setDetailTab] = useState(0);

  useEffect(() => {
    setDetailTab(0);
  }, [selectedRoleId]);

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.rbac} />
        <Box p={3} data-testid="rbac-view">
          <MainTitle title="Roles Management" />
          <Alert severity="info" data-testid="rbac-no-institute">
            Please select an institute from the dashboard to manage RBAC.
          </Alert>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.rbac} />
        <Box p={3} data-testid="rbac-view">
          <MainTitle title="Roles Management" />
          <LinearProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.rbac} />
        <Box p={3} data-testid="rbac-view">
          <MainTitle title="Roles Management" />
          <Alert severity="error">
            Failed to load RBAC data. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.rbac} />
      <Box p={3} data-testid="rbac-view">
        <MainTitle title="Roles Management" />

        <Grid container spacing={3}>
          <Grid item xs={12} md={roles.length > 0 ? 4 : 12}>
            <RoleListView
              roles={roles}
              selectedRoleId={selectedRoleId}
              onSelectRole={setSelectedRoleId}
              onAddRole={() => setAddRoleDialogOpen(true)}
              isLoading={isLoading}
              roleUserCounts={roleUserCounts}
            />
          </Grid>
          {roles.length === 0 ? null : (<Grid item xs={12} md={8}>
            <Card data-testid="rbac-detail-pane">
              <CardContent>
                {!selectedRoleId ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="200px"
                    gap={1}
                    data-testid="rbac-detail-empty"
                  >
                    <Typography variant="body1" color="text.secondary">
                      Select a role to view permissions and assign users
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Tabs
                      value={detailTab}
                      onChange={(_, v) => setDetailTab(v)}
                      sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                      data-testid="rbac-detail-tabs"
                    >
                      <Tab
                        label="Permissions"
                        data-testid="rbac-tab-permissions"
                      />
                      <Tab label="Users" data-testid="rbac-tab-users" />
                    </Tabs>
                    {detailTab === 0 && (
                      <RoleMatrixView
                        roleId={selectedRoleId}
                        instituteId={selectedInstitute.id}
                        onRoleDuplicated={(newRoleId) =>
                          setSelectedRoleId(newRoleId)
                        }
                        onDeleteRole={(role) => {
                          setRoleToDelete(role);
                          setDeleteRoleDialogOpen(true);
                        }}
                      />
                    )}
                    {detailTab === 1 && (
                      <RoleUsersView roleId={selectedRoleId} />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>)}
        </Grid>

        <AddRoleDialog
          open={addRoleDialogOpen}
          onClose={() => setAddRoleDialogOpen(false)}
          onSuccess={() => setAddRoleDialogOpen(false)}
        />
        <DeleteRoleDialog
          open={deleteRoleDialogOpen}
          role={roleToDelete}
          onClose={() => {
            setDeleteRoleDialogOpen(false);
            setRoleToDelete(null);
          }}
          onSuccess={() => {
            setDeleteRoleDialogOpen(false);
            setRoleToDelete(null);
            setSelectedRoleId(null);
          }}
        />
      </Box>
    </>
  );
};

export default RbacView;
